import { useEffect, useRef } from "react";
import { lightHaptic } from "../native/capacitor";

const DEFAULT_EDGE = 24;
const DEFAULT_THRESHOLD = 90;
const DEFAULT_MAX_VERTICAL_DRIFT = 72;
const DRAG_RESISTANCE = 1;
const UNDERLAY_START_SCALE = 0.9;
const UNDERLAY_START_BRIGHTNESS = 0.82;
const UNDERLAY_START_SATURATION = 0.88;
const UNDERLAY_DIM_START_OPACITY = 0.42;
const UNDERLAY_REVEAL_RATIO = 0.88;

export const animateBackAndRun = async ({ target, underlay, dimmer, onBack }) => {
  const targetEl = target?.current || null;
  const underlayEl = underlay?.current || null;
  const dimEl = dimmer?.current || null;
  const screenWidth = Math.max(
    targetEl?.getBoundingClientRect().width || 0,
    window.innerWidth || 0
  );

  if (underlayEl) {
    underlayEl.style.transition = "none";
    underlayEl.style.transform = `scale(${UNDERLAY_START_SCALE})`;
    underlayEl.style.filter = `brightness(${UNDERLAY_START_BRIGHTNESS}) saturate(${UNDERLAY_START_SATURATION})`;
  }

  if (dimEl) {
    dimEl.style.transition = "none";
    dimEl.style.opacity = String(UNDERLAY_DIM_START_OPACITY);
  }

  if (targetEl) {
    targetEl.style.transition = "transform 620ms cubic-bezier(0.22, 1, 0.36, 1)";
    targetEl.style.transform = `translateX(${screenWidth + 36}px)`;
  }

  requestAnimationFrame(() => {
    if (underlayEl) {
      underlayEl.style.transition = "transform 620ms cubic-bezier(0.22, 1, 0.36, 1), filter 620ms cubic-bezier(0.22, 1, 0.36, 1)";
      underlayEl.style.transform = "scale(1)";
      underlayEl.style.filter = "brightness(1) saturate(1)";
    }

    if (dimEl) {
      dimEl.style.transition = "opacity 620ms cubic-bezier(0.22, 1, 0.36, 1)";
      dimEl.style.opacity = "0";
    }
  });

  setTimeout(() => {
    onBack?.();
    requestAnimationFrame(() => {
      if (targetEl) {
        targetEl.style.transition = "";
        targetEl.style.transform = "";
      }
      if (underlayEl) {
        underlayEl.style.transition = "";
        underlayEl.style.transform = "";
        underlayEl.style.filter = "";
      }
      if (dimEl) {
        dimEl.style.transition = "";
        dimEl.style.opacity = "";
      }
    });
  }, 460);

  await lightHaptic();
};

export default function useBackSwipe(onBack, options = {}) {
  const {
    enabled = true,
    edgeWidth = DEFAULT_EDGE,
    threshold = DEFAULT_THRESHOLD,
    maxVerticalDrift = DEFAULT_MAX_VERTICAL_DRIFT,
    target,
    underlay,
    dimmer,
  } = options;

  const stateRef = useRef({
    tracking: false,
    startX: 0,
    startY: 0,
    deltaX: 0,
    deltaY: 0,
    activeElement: null,
    scrollLocked: false,
    previousOverflow: "",
  });

  useEffect(() => {
    if (!enabled || typeof onBack !== "function") return undefined;

    const element = window;

    const getAnimatedElement = () =>
      stateRef.current.activeElement || target?.current || null;

    const getUnderlayElement = () => underlay?.current || null;
    const getDimmerElement = () => dimmer?.current || null;
    const getSwipeWidth = () =>
      Math.max(
        getAnimatedElement()?.getBoundingClientRect().width || 0,
        window.innerWidth || 0
      );

    const applyDragStyle = (animatedEl, px) => {
      if (!animatedEl) return;
      animatedEl.style.transition = "none";
      animatedEl.style.transform = `translateX(${px}px)`;
    };

    const resetStyle = (animatedEl) => {
      if (!animatedEl) return;
      animatedEl.style.transition = "";
      animatedEl.style.transform = "";
    };

    const applyUnderlayState = (progress) => {
      const underlayEl = getUnderlayElement();
      if (!underlayEl) return;

      const p = Math.max(0, Math.min(1, progress));
      const scale = UNDERLAY_START_SCALE + (1 - UNDERLAY_START_SCALE) * p;
      const brightness =
        UNDERLAY_START_BRIGHTNESS + (1 - UNDERLAY_START_BRIGHTNESS) * p;
      const saturation =
        UNDERLAY_START_SATURATION + (1 - UNDERLAY_START_SATURATION) * p;

      underlayEl.style.transition = "none";
      underlayEl.style.transform = `scale(${scale})`;
      underlayEl.style.filter = `brightness(${brightness}) saturate(${saturation})`;

      const dimEl = getDimmerElement();
      if (dimEl) {
        const opacity = Math.max(0, UNDERLAY_DIM_START_OPACITY * (1 - p));
        dimEl.style.transition = "none";
        dimEl.style.opacity = String(opacity);
      }
    };

    const primeUnderlay = () => {
      const underlayEl = getUnderlayElement();
      if (!underlayEl) return;
      underlayEl.style.transition = "transform 120ms ease-out, filter 120ms ease-out";
      underlayEl.style.transform = `scale(${UNDERLAY_START_SCALE})`;
      underlayEl.style.filter = `brightness(${UNDERLAY_START_BRIGHTNESS}) saturate(${UNDERLAY_START_SATURATION})`;

      const dimEl = getDimmerElement();
      if (dimEl) {
        dimEl.style.transition = "opacity 120ms ease-out";
        dimEl.style.opacity = String(UNDERLAY_DIM_START_OPACITY);
      }
    };

    const resetUnderlay = () => {
      const underlayEl = getUnderlayElement();
      if (underlayEl) {
        underlayEl.style.transition = "transform 180ms ease-out, filter 180ms ease-out";
        underlayEl.style.transform = "";
        underlayEl.style.filter = "";
      }

      const dimEl = getDimmerElement();
      if (dimEl) {
        dimEl.style.transition = "opacity 180ms ease-out";
        dimEl.style.opacity = "0";
      }

      setTimeout(() => {
        const active = getUnderlayElement();
        if (active) active.style.transition = "";
        const activeDim = getDimmerElement();
        if (activeDim) activeDim.style.transition = "";
      }, 190);
    };

    const lockScroll = () => {
      if (stateRef.current.scrollLocked) return;
      stateRef.current.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      stateRef.current.scrollLocked = true;
    };

    const unlockScroll = () => {
      if (!stateRef.current.scrollLocked) return;
      document.body.style.overflow = stateRef.current.previousOverflow || "";
      stateRef.current.scrollLocked = false;
      stateRef.current.previousOverflow = "";
    };

    const onTouchStart = (event) => {
      if (!event.touches || event.touches.length !== 1) return;
      const touch = event.touches[0];
      if (touch.clientX > edgeWidth) return;

      const animatedEl = target?.current || null;
      if (!animatedEl) return;

      stateRef.current = {
        tracking: true,
        startX: touch.clientX,
        startY: touch.clientY,
        deltaX: 0,
        deltaY: 0,
        activeElement: animatedEl,
        scrollLocked: false,
        previousOverflow: "",
      };

      primeUnderlay();
    };

    const onTouchMove = (event) => {
      if (!stateRef.current.tracking || !event.touches || event.touches.length !== 1) {
        return;
      }

      const touch = event.touches[0];
      stateRef.current.deltaX = touch.clientX - stateRef.current.startX;
      stateRef.current.deltaY = touch.clientY - stateRef.current.startY;
      const animatedEl = getAnimatedElement();

      const mostlyHorizontal =
        Math.abs(stateRef.current.deltaX) > 10 &&
        Math.abs(stateRef.current.deltaX) > Math.abs(stateRef.current.deltaY) * 1.1;

      if (mostlyHorizontal) {
        lockScroll();
        if (event.cancelable) event.preventDefault();
      }

      if (stateRef.current.deltaX > 0 && mostlyHorizontal) {
        const swipeWidth = getSwipeWidth();
        const revealDistance = swipeWidth * UNDERLAY_REVEAL_RATIO;
        const rawX = Math.min(stateRef.current.deltaX, revealDistance);
        const progress = rawX / revealDistance;
        const offset = Math.min(stateRef.current.deltaX * DRAG_RESISTANCE, swipeWidth + 24);
        applyDragStyle(animatedEl, offset);
        applyUnderlayState(progress);
      }
    };

    const reset = () => {
      stateRef.current.tracking = false;
      stateRef.current.deltaX = 0;
      stateRef.current.deltaY = 0;
      stateRef.current.activeElement = null;
      unlockScroll();
    };

    const onTouchCancel = () => {
      const animatedEl = getAnimatedElement();
      if (animatedEl) {
        animatedEl.style.transition = "transform 140ms ease-out";
        animatedEl.style.transform = "translateX(0px)";
        setTimeout(() => resetStyle(animatedEl), 150);
      }
      resetUnderlay();
      reset();
    };

    const onTouchEnd = async () => {
      if (!stateRef.current.tracking) return;

      const { deltaX, deltaY } = stateRef.current;
      const animatedEl = getAnimatedElement();
      const swipeWidth = getSwipeWidth();
      const effectiveThreshold = Math.max(threshold, swipeWidth * 0.16);
      reset();

      const mostlyHorizontal = Math.abs(deltaX) > Math.abs(deltaY) * 1.2;
      const validVerticalDrift = Math.abs(deltaY) < maxVerticalDrift;
      const crossedThreshold = deltaX > effectiveThreshold;

      if (mostlyHorizontal && validVerticalDrift && crossedThreshold) {
        if (animatedEl) {
          const currentOffset = Math.min(Math.max(deltaX, 0), swipeWidth + 24);
          const targetOffset = swipeWidth + 36;
          const remaining = Math.max(0, targetOffset - currentOffset);
          const remainingRatio = Math.min(1, remaining / Math.max(1, swipeWidth));
          const durationMs = Math.round(560 + remainingRatio * 700);

          animatedEl.style.transition = `transform ${durationMs}ms cubic-bezier(0.22, 1, 0.36, 1)`;
          animatedEl.style.transform = `translateX(${targetOffset}px)`;
          applyUnderlayState(1);
          setTimeout(() => {
            onBack();
            requestAnimationFrame(() => {
              resetStyle(animatedEl);
              resetUnderlay();
            });
          }, Math.round(durationMs * 0.9));
        } else {
          onBack();
          resetUnderlay();
        }
        await lightHaptic();
      } else {
        if (animatedEl) {
          animatedEl.style.transition = "transform 220ms cubic-bezier(0.22, 1, 0.36, 1)";
          animatedEl.style.transform = "translateX(0px)";
          setTimeout(() => {
            resetStyle(animatedEl);
          }, 230);
        }
        resetUnderlay();
      }
    };

    element.addEventListener("touchstart", onTouchStart, { passive: true });
    element.addEventListener("touchmove", onTouchMove, { passive: false });
    element.addEventListener("touchend", onTouchEnd, { passive: true });
    element.addEventListener("touchcancel", onTouchCancel, { passive: true });

    return () => {
      element.removeEventListener("touchstart", onTouchStart);
      element.removeEventListener("touchmove", onTouchMove);
      element.removeEventListener("touchend", onTouchEnd);
      element.removeEventListener("touchcancel", onTouchCancel);
      unlockScroll();
      const animatedEl = getAnimatedElement();
      resetStyle(animatedEl);
      resetUnderlay();
    };
  }, [enabled, onBack, edgeWidth, threshold, maxVerticalDrift, target, underlay, dimmer]);
}
